  // ...imports...
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Subscription } from "rxjs";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  safeSetLocalStorageItem
} from "../../../../packages/logger/src";
import { Logger, formatError } from "../../../../packages/logger/src/Logger";
import {
  IVoiceMailDeleteResponse,
  IVoiceMailReadResponse,
  IVoiceMessageContentResponse,
  IWebexVoicemail,
  RESPONSE_STATUS,
  VM_STORAGE_KEYS,
  VoicemailTranscriptFetchStatus
} from "../../../adapter-interfaces/src";
import { serverSideErrorWhileFetchingVMContentMetric, voicemail, voicemailDeletedMetric, voicemailPlayedFailureMETRIC, voicemailPlayedSuccessMETRIC, voicemailServerSideErrorResponseStatus, voicemailServerSideErrorResponseStatusCode } from "../../src/utils/MetricUtils";
// Custom metrics for voicemail scrolling and pagination
const voicemailScrollLagMetric = "voicemail_scroll_lag_ms";
const voicemailPaginationMetric = "voicemail_pagination_count";
import { AdapterContext } from "../contexts/AdapterContext";
import { submitMetrics } from "../services/WebexMetricsService";
import { CALLING_BACKEND, getCallingBackEnd } from "../utils/callingUtils";
import { CustomError } from '../utils/CustomError';
import { VoicemailTranscriptKind } from "../utils/voicemailTranscript";


const VM_CACHE_KEY_BASE = VM_STORAGE_KEYS.VM_CACHED_DATA;

const isVoicemailMutationSuccess = (res?: IVoiceMailReadResponse) =>
  Boolean(
    res &&
      (res.message === RESPONSE_STATUS.SUCCESS ||
        res.statusCode === RESPONSE_STATUS.STATUSCODE_200 ||
        res.statusCode === RESPONSE_STATUS.STATUSCODE_204)
  );

const applyVoicemailReadStateOverrides = (
  voicemails: IWebexVoicemail[],
  overrides: Map<string, boolean>,
  retireWhenMatched = false
): IWebexVoicemail[] => {
  if (overrides.size === 0) {
    return voicemails;
  }

  return voicemails.map((voicemail) => {
    const unread = overrides.get(voicemail.id);
    if (unread === undefined) {
      return voicemail;
    }
    if (voicemail.unread === unread) {
      if (retireWhenMatched) {
        overrides.delete(voicemail.id);
      }
      return voicemail;
    }
    return { ...voicemail, unread };
  });
};

function buildVoicemailCacheKey(userID?: string): string | undefined {
  return userID
    ? `${VM_CACHE_KEY_BASE}:${encodeURIComponent(userID)}`
    : undefined;
}

/**
 * Custom hook that returns voicemail data for a given user.
 *
 * @returns {object} Returns {callHistory, lastUpdated} values
 */
export default function useVoicemails(userID?: string) {
  // (moved below ctx)
  const [voicemails, setVoicemails] = useState<IWebexVoicemail[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [loading, setLoading] = useState<boolean>(true);
  const [voicemailId, setVoicemailId] = useState<string>("");
  const [voicemailSrc, setVoicemailSrc] = useState<string>("");
  const [isShowVmPlayError, setIsShowVmPlayError] = useState<boolean>(false);
  const [isShowVmConfigPage, setIsShowVmConfigPage] = useState<boolean>(false)
  const [transcripts, setTranscripts] = useState<Record<string, string>>({});
  const [transcriptsLoading, setTranscriptsLoading] = useState<Record<string, boolean>>({});
  const [transcriptKinds, setTranscriptKinds] = useState<Record<string, VoicemailTranscriptKind>>({});
  const [isWebexCallingBackend, setisWebexCallingBackend] = useState<boolean>(false);
  const transcriptValuesRef = useRef<Record<string, string>>({});
  const transcriptInFlightRef = useRef<Set<string>>(new Set());
  const transcriptSubscriptionsRef = useRef<Map<string, Subscription>>(new Map());
  const transcriptAdapterGenerationRef = useRef(0);
  const isMountedRef = useRef(true);
  const readStateOverridesRef = useRef<Map<string, boolean>>(new Map());
  const { t } = useTranslation('WebexVoicemail');
  const ctx = useContext(AdapterContext);
  const voicemailCacheKey = buildVoicemailCacheKey(userID);

  const resetTranscriptState = useCallback(() => {
    transcriptAdapterGenerationRef.current += 1;
    transcriptSubscriptionsRef.current.forEach((subscription) => {
      subscription.unsubscribe();
    });
    transcriptSubscriptionsRef.current.clear();
    transcriptInFlightRef.current.clear();
    transcriptValuesRef.current = {};
    if (isMountedRef.current) {
      setTranscripts({});
      setTranscriptsLoading({});
      setTranscriptKinds({});
      setisWebexCallingBackend(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      resetTranscriptState();
    };
  }, [resetTranscriptState]);

  useEffect(() => {
    resetTranscriptState();
  }, [ctx?.voicemailAdapter, resetTranscriptState, userID]);

  useEffect(() => {
    // The adapter is shared by the desktop and mobile shells and replays its
    // current list. Reset it before subscribing for a new identity so a
    // previous account's replay cannot be persisted under the new account.
    const adapter = ctx?.voicemailAdapter as any;
    adapter?.resetInfiniteScroll?.();
    setVoicemails([]);
    setLoading(Boolean(userID));
    setVoicemailId("");
    setVoicemailSrc("");
    setIsShowVmConfigPage(false);
  }, [ctx?.voicemailAdapter, userID]);

  // Infinite scroll: fetch next batch (after ctx is defined)
  const fetchNextBatch = useCallback(async () => {
    const adapter = ctx?.voicemailAdapter as any;
    if (adapter && typeof adapter.fetchNextBatchAsync === 'function') {
      return adapter.fetchNextBatchAsync();
    }
    if (adapter && typeof adapter.fetchNextBatch === 'function') {
      const start = performance.now();
      const result = await adapter.fetchNextBatch();
      const end = performance.now();
      const lag = end - start;
      // Defer scroll lag metric submission to avoid blocking UI
      /*setTimeout(() => {
        submitMetrics({
          metricEvent: voicemailScrollLagMetric,
          messageId: JSON.stringify({
            lagMs: lag,
            timestamp: Date.now(),
          })
        });
      }, 0);
      // After fetching, count the number of voicemails shown (pagination)
      // Use the latest voicemails state after setVoicemails is called
      setTimeout(() => {
        submitMetrics({
          metricEvent: voicemailPaginationMetric,
          messageId: JSON.stringify({
            count: (Array.isArray(voicemails) ? voicemails.length : 0),
            timestamp: Date.now(),
          })
        });
      }, 0);*/
      return result;
    }
    return undefined;
  }, [ctx?.voicemailAdapter, voicemails]);

  // Infinite scroll: reset state
  const resetInfiniteScroll = useCallback(() => {
    const adapter = ctx?.voicemailAdapter as any;
    if (adapter && typeof adapter.resetInfiniteScroll === 'function') {
      adapter.resetInfiniteScroll();
    }
  }, [ctx?.voicemailAdapter]);

  const onError = useCallback((error: unknown) => {
    setLoading(false);
    const customError = error as CustomError;
    switch (customError?.statusCode) {
      case RESPONSE_STATUS.STATUSCODE_400:
        if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.WXC) {
          setIsShowVmConfigPage(true)
        }
        break;
      case RESPONSE_STATUS.STATUSCODE_404:
        if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.UCM) {
          setIsShowVmConfigPage(true)
        }
        break;
      default:
        setLoading(false);
        Logger.info(
          `WebexVoicemailHook.getVoiceMailList(): Unhandled status code: ${customError?.statusCode} - ${customError?.message}`
        );
        break;
    }
    Logger.error(
      `Could not find voice mail data: '${formatError(customError.message)}'.`
    );
  }, []);

  const persistScopedVoicemailCache = useCallback((data: IWebexVoicemail[]) => {
    removeLocalStorageItem(VM_CACHE_KEY_BASE);
    if (!voicemailCacheKey) {
      return;
    }
    const ok = safeSetLocalStorageItem(voicemailCacheKey, data, {
      arrayPath: undefined,
      timestampExtractor: (r: any) => Date.parse(r?.createdTime || r?.createdAt || r?.startTime || '') || 0,
      setWarningFlagKey: 'storageQuotaWarning'
    });
    if (!ok) {
      Logger.info('[Voicemails] persistScopedVoicemailCache: failed to persist voicemail cache');
    }
  }, [voicemailCacheKey]);

  const onUpdate = useCallback((data: IWebexVoicemail[]) => {
    const nextVoicemails = applyVoicemailReadStateOverrides(
      data,
      readStateOverridesRef.current,
      true
    );
    setLastUpdated(new Date(Date.now()));
    setVoicemails(nextVoicemails);
    setIsShowVmConfigPage(false)
    setLoading(false);
    persistScopedVoicemailCache(nextVoicemails);
  }, [persistScopedVoicemailCache]);


  useEffect(() => {
    // Remove data written by older builds and only hydrate a cache belonging
    // to the currently registered Webex user.
    removeLocalStorageItem(VM_CACHE_KEY_BASE);
    const data = voicemailCacheKey
      ? getLocalStorageItem<any>(voicemailCacheKey)
      : null;
    if (data !== null) {
      setLoading(false);
      setVoicemails(Array.isArray(data) ? data.slice(0, 500) : data); // safety trim if previous versions stored more
    }
  }, [voicemailCacheKey]);


  const refresh = useCallback(async () => {
    if (ctx?.voicemailAdapter?.refresh) {
      console.log("refreshing function");
      return ctx?.voicemailAdapter?.refresh();
    }
    return Promise.resolve();
  }, [ctx?.voicemailAdapter]);

  const setVoicemailUnreadState = useCallback(
    (voicemailId: string, unread: boolean, rememberOverride = true) => {
      if (rememberOverride) {
        readStateOverridesRef.current.set(voicemailId, unread);
      } else {
        readStateOverridesRef.current.delete(voicemailId);
      }
      setVoicemails((prevVoicemails) => {
        const nextVoicemails = prevVoicemails.map((voicemail) =>
          voicemail.id === voicemailId ? { ...voicemail, unread } : voicemail
        );
        persistScopedVoicemailCache(nextVoicemails);
        return nextVoicemails;
      });
    },
    [persistScopedVoicemailCache]
  );

  const unreadVoicemail = useCallback(
    (voicemailId: string) => {
      setVoicemailUnreadState(voicemailId, true);

      const adapter = ctx?.voicemailAdapter as any;
      if (typeof adapter?.markVoicemailUnread !== 'function') {
        setVoicemailUnreadState(voicemailId, false, false);
        return;
      }

      const subscription = adapter.markVoicemailUnread(voicemailId).subscribe(
        (res: IVoiceMailReadResponse) => {
          if (isVoicemailMutationSuccess(res)) {
            return;
          }
          setVoicemailUnreadState(voicemailId, false, false);
        },
        () => {
          setVoicemailUnreadState(voicemailId, false, false);
        }
      );

      return () => {
        subscription?.unsubscribe();
      };
    },
    [ctx?.voicemailAdapter, setVoicemailUnreadState]
  );

  const markAsRead = useCallback(
    (voicemailId: string) => {
      setVoicemailUnreadState(voicemailId, false);

      const adapter = ctx?.voicemailAdapter as any;
      if (typeof adapter?.markVoicemailRead !== 'function') {
        setVoicemailUnreadState(voicemailId, true, false);
        return;
      }

      const subscription = adapter.markVoicemailRead(voicemailId).subscribe(
        (res: IVoiceMailReadResponse) => {
          if (isVoicemailMutationSuccess(res)) {
            return;
          }
          setVoicemailUnreadState(voicemailId, true, false);
        },
        () => {
          setVoicemailUnreadState(voicemailId, true, false);
        }
      );

      return () => {
        subscription?.unsubscribe();
      };
    },
    [ctx?.voicemailAdapter, setVoicemailUnreadState]
  );

  const deleteVoicemail = useCallback(
    (voicemailId: string) => {
      console.info(`[useVoicemails] deleteVoicemail() invoked for id=${voicemailId}`);
      const subscription = ctx.voicemailAdapter
        ?.deleteVoicemail(voicemailId)
        .subscribe((res: IVoiceMailDeleteResponse) => {
          console.info(`[useVoicemails] deleteVoicemail response for id=${voicemailId}: message=${res?.message} status=${res?.statusCode}`);
          if (res.message === RESPONSE_STATUS.SUCCESS) {
            setVoicemails((prevVoicemails) => {
              const nextVoicemails = prevVoicemails.filter((voicemail) => voicemail.id !== voicemailId);
              persistScopedVoicemailCache(nextVoicemails);
              return nextVoicemails;
            });
            submitMetrics(
              {
                metricEvent: voicemailDeletedMetric,
                client_timestamp: voicemail,
                trackingId: voicemail,
                key: voicemail,
              }
            );
            // Do not immediately refresh the entire list here.
            // An immediate full refresh can re-fetch the deleted item if the backend
            // hasn't reconciled the deletion yet and overwrite this optimistic update.
            // The adapter/service or a subsequent backend-driven event will reconcile
            // state when available. If needed, the adapter can emit an updated list
            // after implementing deletion-side effects server-side.
          }
        }, (err) => {
          console.info(`[useVoicemails] deleteVoicemail error for id=${voicemailId}: ${String(err)}`);
        });

      return () => {
        subscription?.unsubscribe();
      };
    },
    [ctx?.voicemailAdapter, persistScopedVoicemailCache]
  );

  const readVoicemail = useCallback(
    (voicemailId: string, unread: boolean) => {
      setIsShowVmPlayError(false);
      setVoicemailSrc("");
      setLocalStorageItem(VM_STORAGE_KEYS.VOICEMAIL_ID_MAP, voicemailId);
      setVoicemailId(
        getLocalStorageItem<string>(VM_STORAGE_KEYS.VOICEMAIL_ID_MAP)
      );
      if (unread) {
        setVoicemailUnreadState(voicemailId, false);
      }
      const subscription = ctx?.voicemailAdapter
        ?.getVoiceMessage(voicemailId)
        .subscribe((res: IVoiceMessageContentResponse) => {
          console.info(`[useVoicemails] getVoiceMessage response for id=${voicemailId}: message=${res?.message} status=${res?.statusCode}`);
          if (res.message === RESPONSE_STATUS.SUCCESS) {
            submitMetrics(
              {
                metricEvent: voicemailPlayedSuccessMETRIC
              }
            );
            const cacheVmId = getLocalStorageItem<string>(
              VM_STORAGE_KEYS.VOICEMAIL_ID_MAP
            );
            const resVmId = res?.voiceMessageId;
            if (cacheVmId === resVmId) {
              const base64String = "data:audio/wav;base64,";
              const audioSrc = `${base64String}${res?.data?.voicemailContent?.content}`;
              setVoicemailSrc(audioSrc);
              if (unread) {
                console.info(`[useVoicemails] markVoicemailRead invoked for id=${resVmId}`);
                const markSub = ctx?.voicemailAdapter
                  ?.markVoicemailRead(resVmId)
                  .subscribe((res: IVoiceMailReadResponse) => {
                    console.info(`[useVoicemails] markVoicemailRead response for id=${resVmId}: message=${res?.message} status=${res?.statusCode}`);
                    if (isVoicemailMutationSuccess(res)) {
                      return;
                    }
                    console.info(`[useVoicemails] markVoicemailRead failed for id=${resVmId}, reverting optimistic state`);
                    setVoicemailUnreadState(resVmId, true, false);
                  }, (err) => {
                    console.info(`[useVoicemails] markVoicemailRead error for id=${resVmId}: ${String(err)}, reverting optimistic state`);
                    setVoicemailUnreadState(resVmId, true, false);
                  });
                // Ensure we unsubscribe to prevent leaks when component unmounts
                // but we are inside callback scope; the outer subscription cleanup will handle it.
              }
            }
          }
          if (res.message === RESPONSE_STATUS.FAILURE) {
            submitMetrics(
              {
                metricEvent: voicemailPlayedFailureMETRIC
              }
            );
            const cacheVmId = getLocalStorageItem<string>(
              VM_STORAGE_KEYS.VOICEMAIL_ID_MAP
            );
            const resVmId = res?.voiceMessageId;
            if (unread) {
              setVoicemailUnreadState(voicemailId, true, false);
            }
            if (cacheVmId === resVmId) {
              submitMetrics(
                {
                  metricEvent: serverSideErrorWhileFetchingVMContentMetric,
                  messageId: `${resVmId}`,
                  responseStatus: voicemailServerSideErrorResponseStatus,
                  voiceMailContentErrorResponseCode: voicemailServerSideErrorResponseStatusCode,
                }
              );
              setIsShowVmPlayError(true);
            }
          }
        }, () => {
          if (unread) {
            setVoicemailUnreadState(voicemailId, true, false);
          }
        });

      return () => {
        subscription?.unsubscribe();
      };
    },
    [ctx?.voicemailAdapter, persistScopedVoicemailCache, setVoicemailUnreadState]
  );

  useEffect(() => {
    const subscription = ctx?.voicemailAdapter
      ?.getAll()
      .subscribe({ next: onUpdate, error: onError });
    const adapter = ctx?.voicemailAdapter as any;
    const errorSubscription =
      typeof adapter?.getErrors === 'function'
        ? adapter.getErrors().subscribe({ next: onError })
        : undefined;

    return () => {
      subscription?.unsubscribe();
      errorSubscription?.unsubscribe();
    };
  }, [ctx?.voicemailAdapter, onError, onUpdate]);

  const onWebexCallingBackendUpdate = useCallback((data:boolean) => {
    setisWebexCallingBackend(data);
  }, []);

  const hasMoreVoicemails = useMemo(() => {
    const adapter = ctx?.voicemailAdapter as any;
    if (adapter && typeof adapter.hasMoreVoicemails === 'function') {
      return Boolean(adapter.hasMoreVoicemails());
    }
    return true;
  }, [ctx?.voicemailAdapter, voicemails]);

  useEffect(() => {
    const webexCallingBackendSubscription = ctx?.voicemailAdapter?.getWebexCallingBackend().subscribe({ next: onWebexCallingBackendUpdate });
    return () => {
      if (webexCallingBackendSubscription) {
        webexCallingBackendSubscription.unsubscribe();
      }
    };
  }, [ctx?.voicemailAdapter, onWebexCallingBackendUpdate]);

  const getVMTranscript = useCallback((voicemailId: string) => {
    const adapter = ctx?.voicemailAdapter;
    if (
      !adapter ||
      transcriptInFlightRef.current.has(voicemailId) ||
      Boolean(transcriptValuesRef.current[voicemailId])
    ) {
      return;
    }

    transcriptSubscriptionsRef.current.get(voicemailId)?.unsubscribe();
    transcriptSubscriptionsRef.current.delete(voicemailId);
    transcriptInFlightRef.current.add(voicemailId);
    const requestGeneration = transcriptAdapterGenerationRef.current;
    if (isMountedRef.current) {
      setTranscriptsLoading((prev) => ({ ...prev, [voicemailId]: true }));
    }

    let settled = false;
    const settle = (
      transcript: string,
      kind: VoicemailTranscriptKind,
      logFailure = false
    ) => {
      if (
        settled ||
        requestGeneration !== transcriptAdapterGenerationRef.current
      ) {
        return;
      }
      settled = true;
      transcriptInFlightRef.current.delete(voicemailId);
      if (kind === 'content') {
        transcriptValuesRef.current[voicemailId] = transcript;
      } else {
        delete transcriptValuesRef.current[voicemailId];
      }
      if (isMountedRef.current) {
        setTranscriptsLoading((prev) => ({
          ...prev,
          [voicemailId]: false,
        }));
        setTranscripts((prev) => ({ ...prev, [voicemailId]: transcript }));
        setTranscriptKinds((prev) => ({ ...prev, [voicemailId]: kind }));
      }
      const activeSubscription =
        transcriptSubscriptionsRef.current.get(voicemailId);
      transcriptSubscriptionsRef.current.delete(voicemailId);
      activeSubscription?.unsubscribe();
      if (logFailure) {
        Logger.info('[Voicemail] Transcript request failed');
      }
    };

    const subscription = adapter.getVMTranscript(voicemailId).subscribe({
      next: (res) => {
        switch(res.message) {
          case VoicemailTranscriptFetchStatus.READY: {
            const raw = res?.data?.voicemailTranscript ?? '';
            const normalized = typeof raw === 'string' ? raw.trim() : '';
            if (normalized.length > 0) {
              settle(normalized, 'content');
            } else {
              settle(t("vmTranscription.na"), 'status');
            }
            break;
          }
          case VoicemailTranscriptFetchStatus.FAILURE:
            settle(t("vmTranscription.failure"), 'status');
            break;
          case VoicemailTranscriptFetchStatus.PENDING:
            settle(t("vmTranscription.pending"), 'status');
            break;
          case VoicemailTranscriptFetchStatus.ERROR:
            settle(t("vmTranscription.error"), 'status');
            break;
          case VoicemailTranscriptFetchStatus.NA:
            settle(t("vmTranscription.na"), 'status');
            break;
          default:
            settle(t("vmTranscription.error"), 'status', true);
        }
      },
      error: () => {
        settle(t("vmTranscription.error"), 'status', true);
      },
      complete: () => {
        if (!settled) {
          settle(t("vmTranscription.error"), 'status', true);
        }
      },
    });

    if (settled || subscription.closed) {
      subscription.unsubscribe();
    } else {
      transcriptSubscriptionsRef.current.set(voicemailId, subscription);
    }
  }, [ctx?.voicemailAdapter, t]);

  return {
    lastUpdated,
    voicemails,
    refresh,
    loading,
    deleteVoicemail,
    readVoicemail,
    unreadVoicemail,
    markAsRead,
    voicemailSrc,
    voicemailId,
    isShowVmPlayError,
    isShowVmConfigPage,
    transcripts,
    transcriptKinds,
    getVMTranscript,
    transcriptsLoading,
    isWebexCallingBackend,
    fetchNextBatch,
    hasMoreVoicemails,
    resetInfiniteScroll,
  } as const;
}
